import * as angular from "angular";
import * as moment from "moment";
import * as Consts from "../ts/rlAdmin.consts";

export abstract class BaseCacheStorageService {

    protected keyPrefix: string;
    keyExpirationKey = "key_expiration";
    cacheEnabledKey = "rl_lcsenabled";
    cacheEnabled: boolean;
    keyExpirations: { [key: string]: string };
    promiseCache: { [key: string]: ng.IPromise<any> } = {};

    constructor(keyPrefix: string, private storage: Storage, private $log: ng.ILogService, private $q: ng.IQService) {
        this.keyPrefix = keyPrefix;
        this.setup();
    }


    private setup() {
        this.scavenge();
        this.cacheEnabled = (!!JSON.parse(this.storage.getItem(this.cacheEnabledKey)) || this.storage.getItem(this.cacheEnabledKey) == null) && Consts.Caching.Enabled;
        if (!this.cacheEnabled) {
            this.$log.warn("RL Cache disabled");
        }
        this.keyExpirations = JSON.parse(this.storage.getItem(this.buildKey(this.keyExpirationKey))) || {};
        //this.$log.info(this.keyExpirations);
    }

    private scavenge() {
        var nowFormated = moment().format();
        var keysMutated = false;
        _(this.keyExpirations)
            .keys()
            .filter((key) => this.keyExpirations[key] != null && this.keyExpirations[key] < nowFormated)
            .forEach((key) => {
                //this.$log.log(`expiring key ${key}`);
                delete this.keyExpirations[key];
                this.storage.removeItem(key);
                keysMutated = true;
            })
            .value();
        if (keysMutated) {
            this.storeKeyExpirations();
        }
    }

    private buildKey(key: string) {
        return `${this.keyPrefix}${key}_${Consts.userDivAppUrlHash}`;
    }

    private setKeyExpiration(key: string, expirationAt: moment.Moment) {
        this.keyExpirations[this.buildKey(key)] = expirationAt.format();
        this.storeKeyExpirations();
    }

    private storeKeyExpirations() {
        this.storage.setItem(this.buildKey(this.keyExpirationKey), JSON.stringify(this.keyExpirations));
    }

    setItem<T>(key: string, item: T, expiresIn: moment.Duration) {
        this.scavenge();
        var expiresAtMoment: moment.Moment = moment().add(expiresIn);
        try {
            this.setKeyExpiration(key, expiresAtMoment);
            this.storage.setItem(this.buildKey(key), JSON.stringify(item));
        } catch (error) {
            // means we've over stuffed the cache
            console.warn("cache full, cutting in half");
            this.cutInHalf();
        }
    }

    private cutInHalf() {
        _(this.keyExpirations)
            .keys()
            .filter(i => Math.random() > .5)
            .forEach(key => {
                delete this.keyExpirations[key];
                this.storage.removeItem(key);
            })
            .value();
        this.storeKeyExpirations();
    }

    getItem<T>(key: string): T {
        var internalKey = this.buildKey(key);
        if (this.keyExpirations[internalKey] == null) {
            //no expiration found
            this.storage.removeItem(internalKey);
        }
        this.scavenge();

        if (!this.cacheEnabled) {
            return null;
        }
        var value = this.storage.getItem(internalKey);
        if (value == null) {
            return null;
        }
        return <T>JSON.parse(value);
    }

    setCacheEnabled(enabled: boolean) {
        this.cacheEnabled = enabled;
        this.storage.setItem(this.cacheEnabledKey, JSON.stringify(enabled));
    }

    setDateTime(key: string, value: moment.Moment, expiresIn: moment.Duration) {
        this.setItem<string>(key, value.format(), expiresIn);
    }

    getDateTime(key: string) {
        var cachedValue = this.getItem<string>(key);
        if (cachedValue == null) {
            return null;
        } else {
            return moment(cachedValue);
        }
    }

    getTimeSince(key: string): moment.Duration {
        var now = moment();
        var then = this.getDateTime(key);
        if (then == null) {
            return moment.duration(9999, "year");
        } else {
            return moment.duration(now.diff(then));
        }
    }

    removeItem(key: string) {
        var internalKey = this.buildKey(key);
        this.storage.removeItem(internalKey);
        if (this.keyExpirations[internalKey] != null) {
            delete this.keyExpirations[internalKey];
            this.storeKeyExpirations();
        }
    }

    clearAll() {
        _(this.keyExpirations)
            .keys()
            .forEach((key) => {
                this.storage.removeItem(key);
            })
            .value();
        this.keyExpirations = {};
        this.storeKeyExpirations();
    }

    getCachedOrDoCache<T>(key: string, func: () => T, expiresIn: moment.Duration) {
        if (!this.cacheEnabled) {
            return func();
        }
        var cachedResult = this.getItem<T>(key);
        if (cachedResult != null) {
            return cachedResult;
        } else {
            var result = func();
            this.setItem<T>(key, result, expiresIn);
            return result;
        }
    }

    getCachedOrDoCachePromise<T>(key: string, func: () => ng.IPromise<T>, expiresIn: moment.Duration) {
        if (!this.cacheEnabled) {
            return func();
        }
        var cachedResult = this.getItem<T>(key);
        if (cachedResult != null) {
            return this.$q.when(cachedResult);
        } else {
            if (this.promiseCache[key] == null) {
                this.promiseCache[key] = func();
                this.promiseCache[key]
                    .then((results) => {
                        this.setItem<T>(key, results, expiresIn);
                    }, angular.noop)
                    .finally(() => {
                        delete this.promiseCache[key];
                    });
            }
            return <ng.IPromise<T>>this.promiseCache[key];
        }
    }
}
